/**
 * The replica's operations (sync protocol §4).
 *
 * An in-memory `Manifest` is the working copy -- every read, search and count runs
 * against it, which is what makes them instant and offline (NFR-PERF-2, -3, -6).
 * Dexie is the durable copy, and the two are kept in step by writing both in the
 * same call.
 *
 * The rule that matters, from §4: **one transaction carries the projection update
 * and the outbox append together.** A crash between them would leave either a
 * local change the server never hears about, or a queued event the projection does
 * not reflect. Putting them in one transaction makes neither representable, which
 * is what FR-10.5 asks for.
 */
import {
  affectedBy,
  anyEvent,
  applyEvent,
  emptyManifest,
  SearchIndex,
  searchTermsFor,
  type AnyEvent,
  type Manifest,
  type SupersededField,
} from '@intentionalist/shared';
import type { Table } from 'dexie';
import {
  entityToRow,
  itemToRow,
  META_CURSOR,
  META_LAST_SYNC,
  META_REVIEW_SESSION,
  META_USER,
  ReplicaDb,
  rowToEntity,
  releaseToRow,
  rowToItem,
  rowToTag,
  tagToRow,
  type EntityRow,
  type OutboxRow,
} from './db.js';

/** Dexie tables an item's children live in, written together with the item. */
const CHILD_TABLES = ['intentions', 'itemTags', 'photos', 'releases'] as const;

const ENTITY_TABLE = {
  category: 'categories',
  location: 'locations',
  customField: 'customFields',
  investment: 'investments',
} as const;

export class Replica {
  /** The working copy. Reads never touch IndexedDB. */
  readonly manifest: Manifest = emptyManifest();

  /**
   * The free-text index over that manifest (FR-3.2).
   *
   * Kept here because it is derived from the manifest and must not be allowed to
   * disagree with it: every path that changes one changes the other, in the same
   * method, rather than leaving a caller to remember.
   */
  readonly index = new SearchIndex();

  constructor(readonly db: ReplicaDb = new ReplicaDb()) {}

  /**
   * Rebuilds the in-memory manifest from IndexedDB.
   *
   * Reads whole tables rather than querying: the replica is sized to fit in
   * memory by design (A7), and a single pass is cheaper than per-item lookups.
   */
  async hydrate(): Promise<Manifest> {
    const [
      items,
      intentions,
      itemTags,
      photos,
      releases,
      tags,
      categories,
      locations,
      fields,
      investments,
    ] = await Promise.all([
      this.db.items.toArray(),
      this.db.intentions.toArray(),
      this.db.itemTags.toArray(),
      this.db.photos.toArray(),
      this.db.releases.toArray(),
      this.db.tags.toArray(),
      this.db.categories.toArray(),
      this.db.locations.toArray(),
      this.db.customFields.toArray(),
      this.db.investments.toArray(),
    ]);

    const by = <T extends { itemId: string }>(rows: T[]): Map<string, T[]> => {
      const grouped = new Map<string, T[]>();
      for (const row of rows) {
        const list = grouped.get(row.itemId);
        if (list) list.push(row);
        else grouped.set(row.itemId, [row]);
      }
      return grouped;
    };

    const byIntention = by(intentions);
    const byTag = by(itemTags);
    const byPhoto = by(photos);
    const byRelease = new Map(releases.map((row) => [row.itemId, row]));

    this.manifest.items.clear();
    for (const row of items) {
      this.manifest.items.set(
        row.itemId,
        rowToItem(row, {
          intentions: byIntention.get(row.itemId) ?? [],
          itemTags: byTag.get(row.itemId) ?? [],
          photos: byPhoto.get(row.itemId) ?? [],
          release: byRelease.get(row.itemId),
        }),
      );
    }

    const load = (rows: EntityRow[], into: Map<string, ReturnType<typeof rowToEntity>>) => {
      into.clear();
      for (const row of rows) into.set(row.entityId, rowToEntity(row));
    };

    this.manifest.tags.clear();
    for (const row of tags) this.manifest.tags.set(row.tagId, rowToTag(row));
    load(categories, this.manifest.categories);
    load(locations, this.manifest.locations);
    load(fields, this.manifest.customFieldDefs);
    load(investments, this.manifest.investments);

    // Every event the device has seen, so a replay reaches the same state and a
    // re-delivered event is recognized as a duplicate rather than re-applied.
    this.manifest.seen.clear();
    for (const row of await this.db.eventLog.toArray()) this.manifest.seen.add(row.eventId);

    // Built once at load, as data model §6 describes.
    this.index.rebuild(this.manifest);

    return this.manifest;
  }

  /**
   * Records local edits: projection, log and outbox, atomically.
   *
   * The events are applied to the in-memory manifest first so the UI can return
   * immediately -- §4 puts nothing network-bound on this path, which is what makes
   * NFR-PERF-1's 500 ms acknowledgment independent of connectivity.
   */
  async write(events: readonly AnyEvent[]): Promise<SupersededField[]> {
    const superseded: SupersededField[] = [];
    for (const event of events) {
      superseded.push(...applyEvent(this.manifest, event).superseded);
    }

    await this.durably(
      [
        this.db.items,
        ...CHILD_TABLES.map((t) => this.db[t]),
        this.db.tags,
        this.db.categories,
        this.db.locations,
        this.db.customFields,
        this.db.investments,
        this.db.eventLog,
        this.db.outbox,
      ],
      async () => {
        await this.persist(events);
        await this.db.eventLog.bulkPut(
          events.map((event) => ({
            key: `${event.hlc}#${event.eventId}`,
            hlc: event.hlc,
            eventId: event.eventId,
            event,
          })),
        );
        // In the same transaction as the projection, deliberately. This is the
        // only copy of these events anywhere until the server accepts them.
        await this.db.outbox.bulkPut(
          events.map((event) => ({
            eventId: event.eventId,
            hlc: event.hlc,
            event,
            queuedAt: Date.now(),
            attempts: 0,
          })),
        );
      },
    );

    this.reindex(events);
    return superseded;
  }

  /**
   * Runs a transaction, and on failure brings the in-memory manifest back to what
   * is actually durable.
   *
   * Events are applied to memory before the transaction so the UI need not wait.
   * If the write then fails, memory holds a change that disk does not, and the
   * person is shown an edit that disappears on the next reload -- worse than an
   * error, because they would believe it had been saved.
   */
  private async durably(tables: Table[], body: () => Promise<void>): Promise<void> {
    try {
      await this.db.transaction('rw', tables, body);
    } catch (error) {
      await this.hydrate();
      throw error;
    }
  }

  /** Events from a pull: projection, log and cursor, atomically. */
  async applyPulled(events: readonly AnyEvent[], cursor: string): Promise<void> {
    for (const event of events) applyEvent(this.manifest, event);

    await this.durably(
      [
        this.db.items,
        ...CHILD_TABLES.map((t) => this.db[t]),
        this.db.tags,
        this.db.categories,
        this.db.locations,
        this.db.customFields,
        this.db.investments,
        this.db.eventLog,
        this.db.meta,
      ],
      async () => {
        await this.persist(events);
        await this.db.eventLog.bulkPut(
          events.map((event) => ({
            key: `${event.hlc}#${event.eventId}`,
            hlc: event.hlc,
            eventId: event.eventId,
            event,
          })),
        );
        // The cursor advances only inside the transaction that applied the page.
        // A failure part way leaves it where it was, so the re-pull is a no-op
        // rather than a gap -- correctness case 9.
        await this.db.meta.put({ key: META_CURSOR, value: cursor });
        await this.db.meta.put({ key: META_LAST_SYNC, value: Date.now() });
      },
    );

    // After the write, not before: a page that failed to land must not leave the
    // index describing events the manifest no longer reflects.
    this.reindex(events);
  }

  /**
   * Brings the search index back in step with the events just applied.
   *
   * An item event re-indexes that item. A *tag* event is the awkward case: a
   * rename or a merge changes the searchable text of every item carrying that
   * tag, not of the tag itself, so those items have to be found and re-indexed.
   * Tag edits are rare, which is what makes the scan acceptable; item edits are
   * constant, which is why they are not scanned for.
   */
  private reindex(events: readonly AnyEvent[]): void {
    const items = new Set<string>();
    let termsChanged = false;

    for (const event of events) {
      const { kind, id } = affectedBy(event);
      if (kind === 'item') items.add(id);
      // A tag, category or location edit changes the searchable text of the
      // *items* carrying it rather than of the record itself -- and for a
      // category, of every item nested beneath it too. Finding which ones costs
      // a walk either way, so the whole catalog is re-indexed. These edits are
      // rare, which is what makes that acceptable.
      else if (kind === 'tag' || kind === 'category' || kind === 'location') {
        termsChanged = true;
      }
    }

    if (termsChanged) {
      for (const item of this.manifest.items.values()) {
        this.index.index(item, searchTermsFor(this.manifest, item));
      }
      return;
    }

    for (const itemId of items) {
      const item = this.manifest.items.get(itemId);
      if (item === undefined) this.index.remove(itemId);
      else this.index.index(item, searchTermsFor(this.manifest, item));
    }
  }

  /** Writes the entities the given events touched, from the in-memory manifest. */
  private async persist(events: readonly AnyEvent[]): Promise<void> {
    const items = new Set<string>();
    const tags = new Set<string>();
    const entities = new Map<keyof typeof ENTITY_TABLE, Set<string>>();

    for (const event of events) {
      const { kind, id } = affectedBy(event);
      if (kind === 'item') items.add(id);
      else if (kind === 'tag') tags.add(id);
      else {
        const set = entities.get(kind) ?? new Set<string>();
        set.add(id);
        entities.set(kind, set);
      }
    }

    for (const itemId of items) {
      const item = this.manifest.items.get(itemId);
      if (item === undefined) continue;
      await this.db.items.put(itemToRow(item));

      // Children are replaced rather than diffed. There are tens of them per
      // item, and a diff is a chance to leave a stale row behind.
      await Promise.all([
        this.db.intentions.where('itemId').equals(itemId).delete(),
        this.db.itemTags.where('[itemId+tagId]').between([itemId, ''], [itemId, '￿']).delete(),
        this.db.photos.where('itemId').equals(itemId).delete(),
      ]);

      // Written rather than deleted-and-rewritten: there is at most one, and its
      // absence is meaningful (no release recorded) rather than merely empty.
      if (item.release !== null || item.releaseDecision !== null) {
        await this.db.releases.put(releaseToRow(item));
      }

      await Promise.all([
        this.db.intentions.bulkPut(
          [...item.intentions.values()].map((record) => ({ itemId, ...record })),
        ),
        this.db.itemTags.bulkPut(
          [...item.tags.entries()].map(([tagId, decision]) => ({ itemId, tagId, ...decision })),
        ),
        this.db.photos.bulkPut([...item.photos.values()].map((photo) => ({ itemId, ...photo }))),
      ]);
    }

    for (const tagId of tags) {
      const tag = this.manifest.tags.get(tagId);
      if (tag !== undefined) await this.db.tags.put(tagToRow(tag));
    }

    for (const [kind, ids] of entities) {
      const source =
        kind === 'category'
          ? this.manifest.categories
          : kind === 'location'
            ? this.manifest.locations
            : kind === 'customField'
              ? this.manifest.customFieldDefs
              : this.manifest.investments;
      for (const id of ids) {
        const record = source.get(id);
        if (record !== undefined) await this.db[ENTITY_TABLE[kind]].put(entityToRow(record));
      }
    }
  }

  // --- outbox -------------------------------------------------------------

  /** Events waiting to be pushed, oldest first, for the count FR-10.4 shows. */
  pending(): Promise<OutboxRow[]> {
    return this.db.outbox.orderBy('hlc').toArray();
  }

  pendingCount(): Promise<number> {
    return this.db.outbox.count();
  }

  /**
   * Clears what the server has taken responsibility for.
   *
   * `rejected` is included: those events will never be accepted, so retrying them
   * forever would wedge the outbox and everything queued behind it.
   */
  async settle(ids: {
    accepted?: readonly string[];
    duplicates?: readonly string[];
    rejected?: readonly string[];
  }): Promise<number> {
    const settled = [...(ids.accepted ?? []), ...(ids.duplicates ?? []), ...(ids.rejected ?? [])];
    if (settled.length === 0) return 0;
    await this.db.outbox.bulkDelete(settled);
    return settled.length;
  }

  /** Records a failed attempt, for backoff. The event itself is never dropped. */
  async noteAttempt(ids: readonly string[]): Promise<void> {
    await this.db.transaction('rw', this.db.outbox, async () => {
      for (const id of ids) {
        const row = await this.db.outbox.get(id);
        if (row) await this.db.outbox.put({ ...row, attempts: row.attempts + 1 });
      }
    });
  }

  // --- bootstrap ----------------------------------------------------------

  /**
   * Replaces the replica with server state (correctness case 11).
   *
   * For a device signing in for the first time, or one recovering from an
   * eviction. Everything is cleared first: a partial overlay would leave rows the
   * server no longer has.
   *
   * **The outbox is deliberately not cleared.** If anything is queued, it is
   * unpushed local work, and discarding it to make the bootstrap tidy would be the
   * data loss of case 12 performed on purpose.
   */
  async loadBootstrap(
    records: readonly ({ kind: string } & Record<string, unknown>)[],
    cursor: string,
  ): Promise<void> {
    await this.db.transaction(
      'rw',
      [
        this.db.items,
        ...CHILD_TABLES.map((t) => this.db[t]),
        this.db.tags,
        this.db.categories,
        this.db.locations,
        this.db.customFields,
        this.db.investments,
        this.db.eventLog,
        this.db.meta,
      ],
      async () => {
        await Promise.all([
          this.db.items.clear(),
          ...CHILD_TABLES.map((t) => this.db[t].clear()),
          this.db.tags.clear(),
          this.db.categories.clear(),
          this.db.locations.clear(),
          this.db.customFields.clear(),
          this.db.investments.clear(),
          this.db.eventLog.clear(),
        ]);

        for (const record of records) await this.putBootstrapRecord(record);
        await this.db.meta.put({ key: META_CURSOR, value: cursor });
        await this.db.meta.put({ key: META_LAST_SYNC, value: Date.now() });
      },
    );

    await this.hydrate();
  }

  /** Maps one server row onto its store, by the `kind` the server tagged it with. */
  private async putBootstrapRecord(
    record: { kind: string } & Record<string, unknown>,
  ): Promise<void> {
    switch (record['kind']) {
      case 'item':
        await this.db.items.put(
          itemToRow(
            rowToItem({
              itemId: record['itemId'] as string,
              record: record as never,
              deletedHlc: '',
              categoryId: '',
              brand: '',
              color: '',
              currentIntention: '',
            }),
          ),
        );
        return;
      case 'intention':
        await this.db.intentions.put(record as never);
        return;
      case 'use':
        // Bootstrap still sends these for data written before use tracking was
        // removed. Accepted and dropped: there is no table to put them in.
        return;
      case 'itemTag':
        await this.db.itemTags.put(record as never);
        return;
      case 'photo':
        await this.db.photos.put(record as never);
        return;
      case 'tag':
        await this.db.tags.put({
          tagId: record['tagId'] as string,
          record: record as never,
        });
        return;
      case 'release':
        await this.db.releases.put({
          itemId: record['itemId'] as string,
          release: (record['release'] ?? null) as never,
          decision: (record['decision'] ?? null) as never,
        });
        return;
      case 'category':
      case 'location':
      case 'customField':
      case 'investment': {
        const table = ENTITY_TABLE[record['kind'] as keyof typeof ENTITY_TABLE] ?? 'categories';
        await this.db[table].put(record as never);
        return;
      }
      default:
        // An unknown kind from a newer server. Ignored rather than fatal: the
        // event log will carry whatever it represents on the next pull.
        return;
    }
  }

  /**
   * Binds this database to a user, clearing it if it belonged to someone else.
   *
   * Returns whether a wipe happened, because the caller then has nothing to pull
   * from and must bootstrap instead.
   *
   * The outbox is cleared too in that case, and only in that case: those events
   * belong to the previous account and pushing them under this one would attribute
   * someone's edits to the wrong person.
   */
  async claim(userId: string): Promise<{ wiped: boolean }> {
    const stored = await this.db.meta.get(META_USER);
    if (stored?.value === userId) return { wiped: false };

    if (stored === undefined) {
      // First use of an empty database; nothing to clear.
      await this.db.meta.put({ key: META_USER, value: userId });
      return { wiped: false };
    }

    await this.db.delete();
    await this.db.open();
    await this.db.meta.put({ key: META_USER, value: userId });
    await this.hydrate();
    return { wiped: true };
  }

  /**
   * The paused review session, if there is one.
   *
   * Returns null rather than throwing on anything unreadable: a session is a
   * convenience, and failing to resume one must never stop the app loading.
   */
  async reviewSession<T>(): Promise<T | null> {
    try {
      const row = await this.db.meta.get(META_REVIEW_SESSION);
      return (row?.value as T | undefined) ?? null;
    } catch {
      return null;
    }
  }

  async saveReviewSession(session: unknown): Promise<void> {
    await this.db.meta.put({ key: META_REVIEW_SESSION, value: session });
  }

  async clearReviewSession(): Promise<void> {
    await this.db.meta.delete(META_REVIEW_SESSION);
  }

  async cursor(): Promise<string> {
    const row = await this.db.meta.get(META_CURSOR);
    return typeof row?.value === 'string' ? row.value : '';
  }

  /**
   * Every event the device holds, in clock order.
   *
   * Read rather than projected, because FR-9.10's "as at a past date" is a
   * replay of a prefix of this log through the same merge -- there is no second,
   * historical store to drift from the live one. The rows are parsed on the way
   * out: they were written by an older version of this app, which makes them
   * input like any other, and a row the current schema rejects is skipped rather
   * than allowed to break every report.
   */
  async log(): Promise<AnyEvent[]> {
    const rows = await this.db.eventLog.orderBy('key').toArray();
    const events: AnyEvent[] = [];
    for (const row of rows) {
      const parsed = anyEvent.safeParse(row.event);
      if (parsed.success) events.push(parsed.data);
    }
    return events;
  }
}
